import { useEffect, useRef, useState } from 'react';
import { 
  MousePointer, Circle, Square, Minus, Trash2, 
  RotateCcw, RotateCw, Save, FolderOpen, Download, Upload, FileUp,
  Ruler, Check, X, RotateCcwSquare, RotateCwSquare, Droplet,
  Pencil, Network, Undo2
} from 'lucide-react';
import { Button } from './ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { Separator } from './ui/separator';
import { cn, formatDistance } from '../lib/utils';
import { PoiMenu } from './PoiMenu';
import { ArrowToolIcon, HoseToolIcon } from './ToolIcons';
import { ARROW_TYPES, LINE_TOOLS, isPathTool } from '../lib/lineStyles';

// Touch-Geräte öffnen Tooltips nicht per Hover: Bezeichnung bei langem Drücken zeigen,
// die Aktion wird dann nicht ausgelöst
const LONG_PRESS_MS = 450;
const LONG_PRESS_HIDE_MS = 1500;

const useLongPressTooltip = () => {
  const [open, setOpen] = useState(false);
  const pressTimer = useRef(null);
  const hideTimer = useRef(null);
  const longPressed = useRef(false);

  useEffect(() => () => {
    clearTimeout(pressTimer.current);
    clearTimeout(hideTimer.current);
  }, []);

  const onPointerDown = (e) => {
    if (e.pointerType !== 'touch') return;
    clearTimeout(pressTimer.current);
    pressTimer.current = setTimeout(() => {
      longPressed.current = true;
      setOpen(true);
    }, LONG_PRESS_MS);
  };

  const onPressEnd = () => {
    clearTimeout(pressTimer.current);
    if (!longPressed.current) return;
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => {
      longPressed.current = false;
      setOpen(false);
    }, LONG_PRESS_HIDE_MS);
  };

  return {
    open,
    // Radix schließt beim Loslassen sofort – nach langem Drücken bleibt die Bezeichnung kurz stehen
    onOpenChange: (next) => { if (!longPressed.current) setOpen(next); },
    isLongPress: () => longPressed.current,
    triggerProps: {
      onPointerDown,
      onPointerUp: onPressEnd,
      onPointerCancel: onPressEnd,
      onPointerLeave: onPressEnd,
      onContextMenu: (e) => e.preventDefault(),
    },
  };
};

const ToolButton = ({ icon: Icon, label, active, onClick, variant = "ghost", disabled, className }) => {
  const tooltip = useLongPressTooltip();

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip open={tooltip.open} onOpenChange={tooltip.onOpenChange}>
        <TooltipTrigger asChild>
          <Button
            variant={active ? "secondary" : variant}
            size="icon"
            onClick={(e) => { if (!tooltip.isLongPress()) onClick?.(e); }}
            disabled={disabled}
            aria-label={label}
            {...tooltip.triggerProps}
            className={cn(
              "h-9 w-9 touch:h-11 touch:w-11 rounded-md transition-all",
              active && "bg-lunefire-red/15 text-lunefire-red border border-lunefire-red/30 hover:bg-lunefire-red/20",
              !active && "hover:bg-lunefire-red/10",
              className
            )}
            data-testid={`tool-${label.toLowerCase().replace(/\s/g, '-')}`}
          >
            <Icon className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="font-body">
          <p>{label}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export const Toolbar = ({ 
  activeTool, 
  onToolChange, 
  onAction,
  canUndo,
  canRedo,
  arrowType,
  onArrowTypeChange,
  hasElements,
  isDrawing,
  drawingPointsCount,
  measurePointsCount,
  measureDistance,
  onFinishDrawing,
  onCancelDrawing,
  onRemoveLastPoint,
  selectedElement,
  onDeleteSelected,
  canRotateSelected,
  onRotateSelected,
  showHydrants,
  onToggleHydrants,
  poiCategories,
  onTogglePoiCategory,
  onSetPoiCategories,
  canEditSelected,
  onEditSelected,
  onOpenFuehrung
}) => {
  const tools = [
    { id: 'select', icon: MousePointer, label: 'Auswählen' },
    { id: 'circle', icon: Circle, label: 'Kreis zeichnen' },
    { id: 'polygon', icon: Square, label: 'Fläche zeichnen (mind. 3 Punkte)' },
    { id: 'polyline', icon: Minus, label: 'Linie zeichnen (mind. 2 Punkte)' },
    { id: 'arrow', icon: ArrowToolIcon, label: 'Richtungspfeil zeichnen (Doppelklick oder Enter beendet)' },
    { id: 'hose', icon: HoseToolIcon, label: 'Schlauchleitung zeichnen' },
    { id: 'measure', icon: Ruler, label: 'Messen' },
  ];

  const canFinish = (LINE_TOOLS.includes(activeTool) && drawingPointsCount >= 2) ||
                   (activeTool === 'polygon' && drawingPointsCount >= 3);

  return (
    <div className="flex items-center gap-1 p-2 overflow-x-auto [&>*]:shrink-0 bg-card/95 backdrop-blur border-b border-lunefire-red/20" data-testid="toolbar">
      {/* Drawing Tools */}
      <div className="flex items-center gap-1">
        {tools.map(tool => (
          <ToolButton
            key={tool.id}
            icon={tool.icon}
            label={tool.label}
            active={activeTool === tool.id}
            onClick={() => onToolChange(tool.id)}
          />
        ))}
      </div>

      <Separator orientation="vertical" className="h-6 mx-2 bg-lunefire-red/20" />

      {/* Drawing Status & Controls */}
      {isPathTool(activeTool) && (
        <>
          {activeTool === 'arrow' && (
            <select
              value={arrowType}
              onChange={(e) => onArrowTypeChange(e.target.value)}
              className="h-9 touch:h-11 rounded-md border border-input bg-background px-2 text-sm font-body"
              aria-label="Pfeilart"
              data-testid="arrow-type-select"
            >
              {Object.entries(ARROW_TYPES).map(([key, type]) => (
                <option key={key} value={key}>{type.label}</option>
              ))}
            </select>
          )}
          <div className="flex items-center gap-2 text-sm text-muted-foreground px-2 font-body">
            <span>{drawingPointsCount} Punkte</span>
          </div>
          <ToolButton
            icon={Undo2}
            label="Letzten Punkt entfernen"
            onClick={onRemoveLastPoint}
            disabled={drawingPointsCount === 0}
          />
          <ToolButton
            icon={Check}
            label="Fertigstellen"
            onClick={onFinishDrawing}
            disabled={!canFinish}
            className="text-green-600 hover:text-green-700 hover:bg-green-100"
          />
          <ToolButton
            icon={X}
            label="Abbrechen"
            onClick={onCancelDrawing}
            disabled={drawingPointsCount === 0}
            className="text-lunefire-red hover:text-lunefire-red hover:bg-lunefire-red/10"
          />
          <Separator orientation="vertical" className="h-6 mx-2 bg-lunefire-red/20" />
        </>
      )}

      {/* Measure Display */}
      {activeTool === 'measure' && measurePointsCount > 0 && (
        <>
          {measureDistance > 0 && (
            <div className="flex items-center gap-2 px-3 py-1 bg-lunefire-orange/10 border border-lunefire-orange/30 rounded-md text-sm font-mono">
              <Ruler className="w-4 h-4 text-lunefire-orange" />
              <span className="text-lunefire-charcoal dark:text-lunefire-yellow font-semibold">
                {formatDistance(measureDistance)}
              </span>
            </div>
          )}
          <ToolButton
            icon={Undo2}
            label="Letzten Punkt entfernen"
            onClick={onRemoveLastPoint}
            disabled={measurePointsCount === 0}
          />
          <ToolButton
            icon={X}
            label="Messung löschen"
            onClick={onCancelDrawing}
            disabled={measurePointsCount === 0}
            className="text-lunefire-red hover:text-lunefire-red hover:bg-lunefire-red/10"
          />
          <Separator orientation="vertical" className="h-6 mx-2 bg-lunefire-red/20" />
        </>
      )}

      {/* Selected Element Delete */}
      {selectedElement && (
        <>
          {canEditSelected && (
            <ToolButton
              icon={Pencil}
              label="Bearbeiten (Doppelklick)"
              onClick={onEditSelected}
            />
          )}
          {canRotateSelected && (
            <>
              <ToolButton
                icon={RotateCcwSquare}
                label="Gegen den Uhrzeigersinn drehen (Shift+R)"
                onClick={() => onRotateSelected(-15)}
              />
              <ToolButton
                icon={RotateCwSquare}
                label="Im Uhrzeigersinn drehen (R)"
                onClick={() => onRotateSelected(15)}
              />
            </>
          )}
          <Button
            variant="destructive"
            size="sm"
            onClick={onDeleteSelected}
            className="h-9 touch:h-11 gap-2 bg-lunefire-red hover:bg-lunefire-red/90 font-display uppercase text-xs tracking-wider"
            data-testid="delete-selected-btn"
          >
            <Trash2 className="h-4 w-4" />
            Löschen
          </Button>
          <Separator orientation="vertical" className="h-6 mx-2 bg-lunefire-red/20" />
        </>
      )}

      {/* Kartenebenen und Führung */}
      <div className="flex items-center gap-1">
        <ToolButton
          icon={Network}
          label="Führungsstruktur, Führungsharke und Funkskizze"
          onClick={onOpenFuehrung}
        />
        <ToolButton
          icon={Droplet}
          label={showHydrants ? 'Hydranten ausblenden' : 'Hydranten anzeigen'}
          active={showHydrants}
          onClick={onToggleHydrants}
        />
        <PoiMenu categories={poiCategories} onToggle={onTogglePoiCategory} onSetAll={onSetPoiCategories} />
      </div>

      <Separator orientation="vertical" className="h-6 mx-2 bg-lunefire-red/20" />

      {/* Actions */}
      <div className="flex items-center gap-1">
        <ToolButton
          icon={RotateCcw}
          label="Rückgängig (Strg+Z)"
          onClick={() => onAction('undo')}
          disabled={!canUndo}
        />
        <ToolButton
          icon={RotateCw}
          label="Wiederholen (Strg+Y)"
          onClick={() => onAction('redo')}
          disabled={!canRedo}
        />
        <ToolButton
          icon={Trash2}
          label="Alle löschen"
          onClick={() => onAction('clear')}
          disabled={!hasElements}
        />
      </div>

      <Separator orientation="vertical" className="h-6 mx-2 bg-lunefire-red/20" />

      {/* File Operations */}
      <div className="flex items-center gap-1">
        <ToolButton
          icon={Save}
          label="Szenario speichern"
          onClick={() => onAction('save')}
        />
        <ToolButton
          icon={FolderOpen}
          label="Szenario laden"
          onClick={() => onAction('load')}
        />
        <ToolButton
          icon={Upload}
          label="GPX importieren"
          onClick={() => onAction('import-gpx')}
        />
        <ToolButton
          icon={FileUp}
          label="Szenario importieren (JSON)"
          onClick={() => onAction('import-json')}
        />
        <ToolButton
          icon={Download}
          label="Szenario exportieren (JSON)"
          onClick={() => onAction('export')}
        />
      </div>
    </div>
  );
};

export default Toolbar;
